import { clsx } from 'clsx';
import {
  BarChart3,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  TrendingUp,
} from 'lucide-react';
import { Card, CardHeader } from '../../components/common/Card';
import { InfoTooltip, TOOLTIPS } from '../../components/common/InfoTooltip';
import {
  EarningsChart,
  RevenueChart,
  CashFlowChart,
  MarginChart,
  EPSGrowthChart,
  ShareDilutionChart,
  SharesOutstandingChart,
  FCFPerShareChart,
} from '../../components/charts/FundamentalsChart';
import { ComparisonChart, type ComparisonDataPoint } from '../../components/charts/ComparisonChart';
import type { FullFundamentalData } from '../../utils/api';

// Format helpers for fundamentals
const formatPercentFund = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '-';
  const pct = value < 1 && value > -1 ? value * 100 : value;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
};

const formatRatio = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '-';
  return value.toFixed(2);
};

const formatCurrencyFund = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '-';
  return `$${value.toFixed(2)}`;
};

// Calculate margin trend from income statement data (comparing latest to previous YEAR)
const getMarginTrendYoY = (
  reports: { fiscal_date?: string; total_revenue?: number; gross_profit?: number; operating_income?: number; net_income?: number }[] | undefined,
  marginType: 'gross' | 'operating' | 'net'
): { trend: 'up' | 'down' | 'neutral' | undefined; change: string | undefined } => {
  if (!reports || reports.length < 2) return { trend: undefined, change: undefined };

  const getMargin = (report: typeof reports[0]): number | undefined => {
    const revenue = report.total_revenue;
    if (!revenue || revenue === 0) return undefined;

    switch (marginType) {
      case 'gross': return report.gross_profit ? report.gross_profit / revenue : undefined;
      case 'operating': return report.operating_income ? report.operating_income / revenue : undefined;
      case 'net': return report.net_income ? report.net_income / revenue : undefined;
    }
  };

  // reports[0] is most recent, reports[1] is previous year
  const currentMargin = getMargin(reports[0]);
  const previousMargin = getMargin(reports[1]);

  if (currentMargin === undefined || previousMargin === undefined) {
    return { trend: undefined, change: undefined };
  }

  // Calculate the percentage point change
  const diff = (currentMargin - previousMargin) * 100; // Convert to percentage points
  const changeStr = `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}pp`;

  if (Math.abs(diff) < 0.1) return { trend: 'neutral', change: changeStr };
  return { trend: diff > 0 ? 'up' : 'down', change: changeStr };
};

// Calculate growth trend YoY
const getGrowthTrendYoY = (
  value: number | undefined | null
): { trend: 'up' | 'down' | 'neutral' | undefined; change: string | undefined } => {
  if (value === null || value === undefined) return { trend: undefined, change: undefined };

  // Value is already a growth rate (e.g., 0.15 = 15%)
  const pct = value < 1 && value > -1 ? value * 100 : value;
  const changeStr = `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;

  if (pct > 0) return { trend: 'up', change: changeStr };
  if (pct < 0) return { trend: 'down', change: changeStr };
  return { trend: 'neutral', change: changeStr };
};

// Calculate ROE/ROA trend YoY from balance sheet data (if available) or just show current performance
const getReturnTrend = (value: number | undefined | null): { trend: 'up' | 'down' | 'neutral' | undefined; change: string | undefined } => {
  if (value === null || value === undefined) return { trend: undefined, change: undefined };
  // ROE > 15% is good, ROA > 5% is good - show as positive trend
  // Since we don't have historical data for these, we just indicate if current is strong
  const pct = value < 1 && value > -1 ? value * 100 : value;
  if (pct > 15) return { trend: 'up', change: undefined };
  if (pct > 0) return { trend: 'neutral', change: undefined };
  return { trend: 'down', change: undefined };
};

// Metric card component for fundamentals
interface MetricCardProps {
  label: string;
  value: string;
  subtext?: string;
  trend?: 'up' | 'down' | 'neutral';
  change?: string; // YoY change like "+2.5%" or "-1.2%"
  tooltip?: string;
  industryAvg?: number | null;
  rawValue?: number | null;
}

function MetricCard({ label, value, subtext, trend, change, tooltip, industryAvg, rawValue }: MetricCardProps) {
  // Calculate over/undervaluation vs sector
  let sectorDiff: number | null = null;
  let sectorColor = 'text-[var(--color-text-muted)]';
  if (industryAvg != null && rawValue != null && industryAvg > 0) {
    sectorDiff = ((rawValue - industryAvg) / industryAvg) * 100;
    if (Math.abs(sectorDiff) <= 10) {
      sectorColor = 'text-[var(--color-text-muted)]';
    } else if (sectorDiff > 0) {
      sectorColor = 'text-red-500'; // overvalued relative to sector
    } else {
      sectorColor = 'text-emerald-500'; // undervalued relative to sector
    }
  }

  return (
    <div className="p-4 rounded-lg bg-[var(--color-bg-secondary)] border border-[var(--color-border)]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          <span className="text-sm text-[var(--color-text-muted)]">{label}</span>
          {tooltip && <InfoTooltip content={tooltip} position="top" />}
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <span className={clsx(
          'text-xl font-bold',
          trend === 'up' && 'text-emerald-500',
          trend === 'down' && 'text-red-500',
          !trend && 'text-[var(--color-text-primary)]'
        )}>
          {value}
        </span>
        {trend && (
          trend === 'up' ? (
            <ArrowUpRight className="h-4 w-4 text-emerald-500" />
          ) : trend === 'down' ? (
            <ArrowDownRight className="h-4 w-4 text-red-500" />
          ) : null
        )}
      </div>
      <div className="flex items-center justify-between mt-1">
        {subtext && (
          <p className="text-xs text-[var(--color-text-muted)]">{subtext}</p>
        )}
        {change && (
          <span className={clsx(
            'text-xs font-medium',
            trend === 'up' && 'text-emerald-500',
            trend === 'down' && 'text-red-500',
            !trend && 'text-[var(--color-text-muted)]'
          )}>
            {change} YoY
          </span>
        )}
      </div>
      {industryAvg != null && (
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--color-border)]">
          <span className="text-[10px] text-[var(--color-text-muted)]">Sector: {industryAvg.toFixed(1)}</span>
          {sectorDiff != null && (
            <span className={clsx('text-[10px] font-medium', sectorColor)}>
              {sectorDiff >= 0 ? '+' : ''}{sectorDiff.toFixed(0)}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface FundamentalsTabProps {
  fundamentalsData: FullFundamentalData | null;
  loadingFundamentals: boolean;
  annualComparisonData: ComparisonDataPoint[];
  quarterlyComparisonData: ComparisonDataPoint[];
  ticker?: string;
}

export function FundamentalsTab({
  fundamentalsData,
  loadingFundamentals,
  annualComparisonData,
  quarterlyComparisonData,
  ticker,
}: FundamentalsTabProps) {
  return (
    <>
      {/* Price vs Fundamentals Comparison - Custom Graph Builder */}
      {(annualComparisonData.length > 0 || quarterlyComparisonData.length > 0) && (
        <Card className="relative">
          <div className="absolute top-4 right-4">
            <InfoTooltip
              content="Compare stock price against fundamental metrics to identify potential disconnects. When fundamentals improve but price drops, it may signal an opportunity."
              position="left"
            />
          </div>
          <CardHeader
            title="Price vs Fundamentals"
            subtitle="Custom comparison chart - Find disconnects between price and value"
            icon={<TrendingUp className="h-5 w-5" />}
          />
          <ComparisonChart
            annualData={annualComparisonData}
            quarterlyData={quarterlyComparisonData}
            weeklyPrices={fundamentalsData?.weekly_prices?.map(p => ({ date: p.date, close: p.close })) ?? []}
            ticker={ticker}
            title="Build Your Comparison"
            defaultPrimaryMetric="stock_price"
            defaultSecondaryMetric="fcf_per_share"
          />
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            <span className="text-emerald-500 font-medium">Tip:</span> Look for divergences where fundamentals improve but price drops.
          </p>
        </Card>
      )}

      {/* ===== FUNDAMENTAL ANALYSIS SECTION ===== */}
      {/* Always show this section - with loading state if data is being fetched */}
      <>
        {loadingFundamentals || !fundamentalsData ? (
          <div className="h-32" />
        ) : (
            <>
              {/* Valuation Metrics */}
              <Card>
                <CardHeader
                  title="Valuation Metrics"
                  subtitle="Price multiples and ratios"
                  icon={<PieChart className="h-5 w-5" />}
                />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <MetricCard
                    label="P/E Ratio"
                    value={formatRatio(fundamentalsData.overview?.pe_ratio)}
                    subtext="TTM"
                    tooltip={TOOLTIPS.peRatio}
                    rawValue={fundamentalsData.overview?.pe_ratio}
                    industryAvg={fundamentalsData.sector_averages?.pe_ratio_median}
                  />
                  <MetricCard
                    label="Forward P/E"
                    value={formatRatio(fundamentalsData.overview?.forward_pe)}
                    subtext="NTM"
                    tooltip={TOOLTIPS.forwardPE}
                    rawValue={fundamentalsData.overview?.forward_pe}
                    industryAvg={fundamentalsData.sector_averages?.forward_pe_median}
                  />
                  <MetricCard
                    label="PEG Ratio"
                    value={formatRatio(fundamentalsData.overview?.peg_ratio)}
                    tooltip={TOOLTIPS.pegRatio}
                    rawValue={fundamentalsData.overview?.peg_ratio}
                    industryAvg={fundamentalsData.sector_averages?.peg_ratio_median}
                  />
                  <MetricCard
                    label="P/B Ratio"
                    value={formatRatio(fundamentalsData.overview?.price_to_book)}
                    tooltip={TOOLTIPS.pbRatio}
                    rawValue={fundamentalsData.overview?.price_to_book}
                    industryAvg={fundamentalsData.sector_averages?.price_to_book_median}
                  />
                  <MetricCard
                    label="P/S Ratio"
                    value={formatRatio(fundamentalsData.overview?.price_to_sales)}
                    tooltip={TOOLTIPS.psRatio}
                    rawValue={fundamentalsData.overview?.price_to_sales}
                    industryAvg={fundamentalsData.sector_averages?.price_to_sales_median}
                  />
                  <MetricCard
                    label="EV/EBITDA"
                    value={formatRatio(fundamentalsData.overview?.ev_to_ebitda)}
                    tooltip={TOOLTIPS.evToEbitda}
                    rawValue={fundamentalsData.overview?.ev_to_ebitda}
                    industryAvg={fundamentalsData.sector_averages?.ev_to_ebitda_median}
                  />
                </div>
              </Card>

              {/* Profitability Metrics */}
              <Card>
                <CardHeader
                  title="Profitability"
                  subtitle="Margin and return metrics (YoY comparison)"
                  icon={<TrendingUp className="h-5 w-5" />}
                />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {(() => {
                    const profitMarginYoY = getMarginTrendYoY(
                      fundamentalsData.income_statement?.annual_reports,
                      'net'
                    );
                    return (
                      <MetricCard
                        label="Profit Margin"
                        value={formatPercentFund(fundamentalsData.overview?.profit_margin)}
                        trend={profitMarginYoY.trend}
                        change={profitMarginYoY.change}
                        tooltip={TOOLTIPS.profitMargin}
                      />
                    );
                  })()}
                  {(() => {
                    const operatingMarginYoY = getMarginTrendYoY(
                      fundamentalsData.income_statement?.annual_reports,
                      'operating'
                    );
                    return (
                      <MetricCard
                        label="Operating Margin"
                        value={formatPercentFund(fundamentalsData.overview?.operating_margin)}
                        trend={operatingMarginYoY.trend}
                        change={operatingMarginYoY.change}
                        tooltip={TOOLTIPS.operatingMargin}
                      />
                    );
                  })()}
                  {(() => {
                    const roeTrend = getReturnTrend(fundamentalsData.overview?.roe);
                    return (
                      <MetricCard
                        label="ROE"
                        value={formatPercentFund(fundamentalsData.overview?.roe)}
                        trend={roeTrend.trend}
                        tooltip={TOOLTIPS.roe}
                      />
                    );
                  })()}
                  {(() => {
                    const roaTrend = getReturnTrend(fundamentalsData.overview?.roa);
                    return (
                      <MetricCard
                        label="ROA"
                        value={formatPercentFund(fundamentalsData.overview?.roa)}
                        trend={roaTrend.trend}
                        tooltip={TOOLTIPS.roa}
                      />
                    );
                  })()}
                  {(() => {
                    const revenueGrowth = getGrowthTrendYoY(fundamentalsData.overview?.revenue_growth);
                    return (
                      <MetricCard
                        label="Revenue Growth"
                        value={formatPercentFund(fundamentalsData.overview?.revenue_growth)}
                        trend={revenueGrowth.trend}
                        tooltip={TOOLTIPS.revenueGrowth}
                      />
                    );
                  })()}
                  {(() => {
                    const earningsGrowth = getGrowthTrendYoY(fundamentalsData.overview?.earnings_growth);
                    return (
                      <MetricCard
                        label="Earnings Growth"
                        value={formatPercentFund(fundamentalsData.overview?.earnings_growth)}
                        trend={earningsGrowth.trend}
                        tooltip={TOOLTIPS.earningsGrowth}
                      />
                    );
                  })()}
                </div>
              </Card>

              {/* Earnings Chart */}
              {fundamentalsData.earnings && (
                <Card className="relative">
                  <div className="absolute top-4 right-4">
                    <InfoTooltip content={TOOLTIPS.earningsHistory} position="left" />
                  </div>
                  <CardHeader
                    title="Earnings History"
                    subtitle="EPS performance over time"
                    icon={<DollarSign className="h-5 w-5" />}
                  />
                  <div className="grid lg:grid-cols-2 gap-6">
                    <EarningsChart
                      data={fundamentalsData.earnings.quarterly_earnings}
                      title="Quarterly EPS vs Estimates"
                      ticker={ticker}
                    />
                    <EPSGrowthChart
                      data={fundamentalsData.earnings.annual_earnings}
                      quarterlyData={fundamentalsData.earnings.quarterly_earnings}
                      forwardEps={fundamentalsData.overview?.eps}
                      title="Annual EPS Trend"
                      ticker={ticker}
                    />
                  </div>
                </Card>
              )}

              {/* Income Statement Charts */}
              {fundamentalsData.income_statement && (
                <Card className="relative">
                  <div className="absolute top-4 right-4">
                    <InfoTooltip content={TOOLTIPS.incomeStatement} position="left" />
                  </div>
                  <CardHeader
                    title="Income Statement"
                    subtitle="Revenue and profitability trends"
                    icon={<BarChart3 className="h-5 w-5" />}
                  />
                  <div className="grid lg:grid-cols-2 gap-6">
                    <RevenueChart
                      data={fundamentalsData.income_statement.quarterly_reports}
                      title="Quarterly Revenue & Profit"
                      ticker={ticker}
                    />
                    <RevenueChart
                      data={fundamentalsData.income_statement.annual_reports}
                      title="Annual Revenue & Profit"
                      ticker={ticker}
                    />
                  </div>
                  <div className="grid lg:grid-cols-2 gap-6 mt-6">
                    <MarginChart
                      data={fundamentalsData.income_statement.quarterly_reports}
                      title="Quarterly Margin Trends"
                      ticker={ticker}
                    />
                    <MarginChart
                      data={fundamentalsData.income_statement.annual_reports}
                      title="Annual Margin Trends"
                      ticker={ticker}
                    />
                  </div>
                </Card>
              )}

              {/* Cash Flow Chart */}
              {fundamentalsData.cash_flow && (
                <Card className="relative">
                  <div className="absolute top-4 right-4">
                    <InfoTooltip content={TOOLTIPS.cashFlow} position="left" />
                  </div>
                  <CardHeader
                    title="Cash Flow"
                    subtitle="Operating, investing, and financing activities"
                    icon={<DollarSign className="h-5 w-5" />}
                  />
                  <div className="grid lg:grid-cols-2 gap-6">
                    <CashFlowChart
                      data={fundamentalsData.cash_flow.quarterly_reports}
                      title="Quarterly Cash Flow"
                      ticker={ticker}
                    />
                    <CashFlowChart
                      data={fundamentalsData.cash_flow.annual_reports}
                      title="Annual Cash Flow"
                      ticker={ticker}
                    />
                  </div>
                </Card>
              )}

              {/* Share Dilution Analysis - Buybacks vs SBC */}
              {(fundamentalsData.cash_flow?.annual_reports?.length > 0 || fundamentalsData.balance_sheet?.annual_reports?.length > 0) && (
                <Card className="relative">
                  <div className="absolute top-4 right-4">
                    <InfoTooltip content={TOOLTIPS.shareDilution} position="left" />
                  </div>
                  <CardHeader
                    title="Share Dilution Analysis"
                    subtitle="Buybacks, SBC, share count, and FCF per share - The complete picture"
                    icon={<PieChart className="h-5 w-5" />}
                  />
                  <div className="grid lg:grid-cols-2 gap-6">
                    {fundamentalsData.cash_flow?.annual_reports && (
                      <ShareDilutionChart
                        data={fundamentalsData.cash_flow.annual_reports}
                        title="Buybacks vs Stock-Based Compensation"
                        ticker={ticker}
                      />
                    )}
                    {fundamentalsData.balance_sheet?.annual_reports && (
                      <SharesOutstandingChart
                        data={fundamentalsData.balance_sheet.annual_reports}
                        sbcData={fundamentalsData.cash_flow?.annual_reports}
                        title="Shares Outstanding Over Time"
                        ticker={ticker}
                      />
                    )}
                  </div>
                  {/* FCF Per Share - The most important metric combining cash flow and share count */}
                  {fundamentalsData.cash_flow?.annual_reports && fundamentalsData.balance_sheet?.annual_reports && (
                    <div className="mt-6 pt-6 border-t border-[var(--color-border)]">
                      <FCFPerShareChart
                        cashFlowData={fundamentalsData.cash_flow.annual_reports}
                        balanceSheetData={fundamentalsData.balance_sheet.annual_reports}
                        title="Free Cash Flow Per Share"
                        ticker={ticker}
                      />
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                    <span className="text-emerald-500 font-medium">FCF/Share</span> is the ultimate measure — growing FCF/Share means more cash per share despite any dilution.
                  </p>
                </Card>
              )}

              {/* Additional Metrics */}
              <Card>
                <CardHeader
                  title="Additional Metrics"
                  subtitle="Key financial indicators"
                  icon={<BarChart3 className="h-5 w-5" />}
                />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <MetricCard
                    label="EPS"
                    value={formatCurrencyFund(fundamentalsData.overview?.eps)}
                    subtext="TTM"
                    tooltip={TOOLTIPS.eps}
                  />
                  <MetricCard
                    label="Dividend Yield"
                    value={formatPercentFund(fundamentalsData.overview?.dividend_yield)}
                    tooltip={TOOLTIPS.dividendYield}
                  />
                  <MetricCard
                    label="Beta"
                    value={formatRatio(fundamentalsData.overview?.beta)}
                    tooltip={TOOLTIPS.beta}
                  />
                  <MetricCard
                    label="52W High"
                    value={formatCurrencyFund(fundamentalsData.overview?.week_52_high)}
                    tooltip={TOOLTIPS.week52High}
                  />
                  <MetricCard
                    label="52W Low"
                    value={formatCurrencyFund(fundamentalsData.overview?.week_52_low)}
                    tooltip={TOOLTIPS.week52Low}
                  />
                  <MetricCard
                    label="Analyst Target"
                    value={formatCurrencyFund(fundamentalsData.overview?.analyst_target_price)}
                    tooltip={TOOLTIPS.analystTarget}
                  />
                </div>
              </Card>
            </>
          )}
        </>
    </>
  );
}
